import React, { useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';

// Components

import ControlsContainer from './ControlsContainer';
import ButtonContainer from './ButtonContainer';

// ActionCreators / Selectors

import {
    adjustFlow,
    changePage,
    receiveImmigrationInfo,
    receiveSSNs,
    removeException,
    updateException
} from '@actions/ssap/ssapActionCreators';

import { fetchCommunicationPreferences } from '@actions/individualPortal/preferences/communicationPreferencesActionCreators';
import { setError } from '@actions/common/actionCreators';


import {
    selectIfAlwaysDisplayMedicaidQuestionsEnabled,
    selectIfInlineEligibilityIgnoredForEscPage,
    selectIfMedicaidTerminationFeatureEnabled,
    selectIsMemberRemovalReasonEnabled,
    selectIsInlineCurrentIncomeEnabled,
    selectIsItfFeatureEnabled,
    selectPrimaryTaxFiler,
    selectIsItuQuestionsDisabled,
    selectMedicaidEndDateOverride,
} from '@reducers/appReducer';

//Utils

import { pageVisibility } from '@utils/miscellaneous/pageAndQuestionsVisibility';
import { debugWatchedProps } from '@utils/miscellaneous/debugWatchedProps';
import { familyAnalyzer } from '@utils/householdMember/familyAnalyzer';
import { filterTools } from '@utils/tools/filterTools';
import { getCurrentHouseholdMember } from '@utils/householdMember/getCurrentHouseholdMember';
import { getWomanOfReproductiveAge } from '@utils/householdMember/getWomanOfReproductiveAge';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { preventerTools } from '@utils/tools/preventerTools';
import { cloneDeep, isEqual } from '@utils/miscellaneous/objectHelpers';
import { getDateDifference } from '@utils/dates/date.utils';

// Constants

import { EXCEPTIONS } from '@constants/ssap/exceptions/ssapExceptions';
import { INCOME_TYPES } from '@constants/ssap/hhmTypes/incomeAndExpenceTypes';
import { PAGES } from '@constants/ssap/flows/pagesTypes';
import { COMMUNICATION_PREFERENCES, IMMIGRATION_INFO, SSNS } from '@APIs/common/endpoints';
import {
    UNEMPLOYMENT_INCOME_ATTESTATION_SUPPORTED_YEARS
} from '@constants/ssap/miscellaneous/miscellaneous';


/**
 *
 * @param {Object} props
 * @returns {React.Component} - Page Container
 *
 */
const PagesContainer = props => {

    const {
        adjustedFlow = [],
        bloodRelationshipCodes,
        data: {
            coverageYear,
            ssapApplicationId,
            taxHousehold,
            taxHousehold: {
                bloodRelationship = [],
                primaryTaxFilerPersonId
            }
        },
        direction,
        disabledUi,
        environment: {
            localDates: {
                serverDate
            },
            isState
        },
        exception: {
            exceptionType,
            exceptionPages
        },
        launchData: {
            flowCreated
        },
        loadedRelations,
        flow: {
            isFlowLoaded
        },
        history,
        householdMembers = [],
        householdMembers: {
            length: hhmsQty
        },
        page,
        scroll
    } = props;

    const {
        hhmIndex,
        hhmGuid,
        currentHhm,
    } = getCurrentHouseholdMember(adjustedFlow, page, householdMembers);

    const [
        defaultHomeAddress = {},
        defaultMailingAddress = {}
    ] = taxHousehold.otherAddresses.filter(a => !a.deletable);

    const { addressId: defaultHomeAddressId } = defaultHomeAddress;
    const { addressId: defaultMailingAddressId } = defaultMailingAddress;

    const alwaysDisplayMedicaidQuestions = useSelector(selectIfAlwaysDisplayMedicaidQuestionsEnabled);
    const itfFeatureEnabled = useSelector(selectIsItfFeatureEnabled);
    const inlineEligibilityIgnoredForEscPage = useSelector(selectIfInlineEligibilityIgnoredForEscPage);
    const inlineCurrentIncomeEnabled = useSelector(selectIsInlineCurrentIncomeEnabled);
    const isItuQuestionsDisabled = useSelector(selectIsItuQuestionsDisabled);
    const memberRemovalReasonEnabled = useSelector(selectIsMemberRemovalReasonEnabled);
    const primaryTaxFiler = useSelector(selectPrimaryTaxFiler);
    const medicaidTerminationFeatureEnabled = useSelector(selectIfMedicaidTerminationFeatureEnabled);
    const medicaidEndDateOverride = useSelector(selectMedicaidEndDateOverride);

    const familyConfig = {
        bloodRelationships: bloodRelationship,
        bloodRelationshipCodes,
        currentMemberIndex: hhmIndex,
        householdMembers,
        isState,
        loadedRelations,
        primaryTaxFilerPersonId,
        serverDate
    };

    const allStatuses = householdMembers.map((hhm, idx) => ({
        isCurrentHhm: hhmGuid === hhm.applicantGuid,
        hhmGuid: hhm.applicantGuid,
        hhmStatus: familyAnalyzer({ ...familyConfig, currentMemberIndex: idx })
    }));

    const familyStatus = hhmIndex !== null ? allStatuses[hhmIndex].hhmStatus : { currentHhm: {} };

    const watchedHhmsProps = householdMembers.map((hhm, idx) => {

        const {
            addressId,
            americanIndianAlaskaNative: {
                americanIndianAlaskaNativeSelfIdentification,
                memberOfFederallyRecognizedTribeIndicator
            },
            applyingForCoverageIndicator,
            citizenshipImmigrationStatus: {
                livedIntheUSSince1996Indicator
            },
            dateOfBirth,
            gender,
            householdIncomeOrSizeChanged,
            incomes,
            medicaidChipExpirationDate,
            medicaidTermination,
            name,
            taxHouseholdComposition: {
                married,
                taxFiler,
                taxRelationship
            }
        } = hhm;

        const hhmsProps = {
            addressId,
            americanIndianAlaskaNativeSelfIdentification,
            memberOfFederallyRecognizedTribeIndicator,
            applyingForCoverageIndicator,
            dateOfBirth,
            gender,
            householdIncomeOrSizeChanged,
            'incomes.length': incomes.length,
            livedIntheUSSince1996Indicator,
            married,
            medicaidChipExpirationDate,
            medicaidTermination,
            taxFiler,
            taxRelationship
        };

        return `HOUSEHOLD_MEMBER[${ normalizeNameOfPerson(name) }, index: ${ idx }]_PROPS:` +
            Object
                .entries(hhmsProps)
                .reduce(
                    (res, [ key, value ]) => {
                        return [ res, `${key} : ${value}` ].join(' ::: ');
                    },
                    ''
                );
    }).join(' ::: ');

    const watchedProps = Object.entries({
        flow: adjustedFlow.map(p => p.isActive).join(', '),
        flowLength: adjustedFlow.length,
        bloodRelationships: bloodRelationship.map(br => br.relation).join(','),
        exceptionType: exceptionType,
        hhmsQty: hhmsQty,
        primaryTaxFilerPersonId: primaryTaxFilerPersonId,
        watchedHhmsProps: watchedHhmsProps
    }).reduce((res, [ key, value ]) => {
        return [ res, `${key} : ${value}` ].join(' ::: ');
    } , '');

    const [ isPageDataReady, togglePageReadiness ] = React.useState(false);
    const [ isExtraDataRetrieved, toggleExtraDataStatus ] = React.useState(null);

    useEffect(() => { // Exceptions remover
        const conditions = [
            [
                EXCEPTIONS.EDITING_INCOMES_AND_DEDUCTIONS,
                PAGES.PERSONAL_INCOME_SUMMARY_PAGE,
            ],
            [
                EXCEPTIONS.REVIEW_INCOME_SECTION,
                PAGES.INCOME_INFORMATION_SUMMARY_PAGE
            ],
            [
                EXCEPTIONS.EDITING_HOUSEHOLD_MEMBER,
                PAGES.HOUSEHOLD_SUMMARY_PAGE
            ],
            [
                EXCEPTIONS.EDITING_HOUSEHOLD_MEMBER_ADDRESS,
                PAGES.HOUSEHOLD_SUMMARY_PAGE
            ],
            [
                EXCEPTIONS.EDITING_PRIMARY_HOUSEHOLD_CONTACT,
                PAGES.HOUSEHOLD_SUMMARY_PAGE
            ],
            [
                EXCEPTIONS.REVIEW_HOUSEHOLD_SECTION,
                PAGES.FAMILY_AND_HOUSEHOLD_SUMMARY_PAGE
            ],
            [
                EXCEPTIONS.REVIEW_BEFORE_SUBMIT,
                PAGES.FINAL_APPLICATION_REVIEW_PAGE
            ],
            [
                EXCEPTIONS.REVIEW_BEFORE_SUBMIT_FAMILY_AND_HOUSEHOLD,
                PAGES.FINAL_APPLICATION_REVIEW_PAGE
            ],
            [
                EXCEPTIONS.FIXING_TAX_RELATIONSHIP,
                PAGES.TAX_HOUSEHOLD_COMPOSITION_PAGE
            ],
            [
                EXCEPTIONS.EDITING_ADDITIONAL_INFORMATION,
                PAGES.ADDITIONAL_INFORMATION_SUMMARY_PAGE
            ],
            [
                EXCEPTIONS.MANAGE_SPOUSE,
                [
                    PAGES.HOUSEHOLD_SUMMARY_PAGE,
                    PAGES.MARITAL_STATUS
                ]
            ]
        ];
        conditions.forEach(([ exceptionTypeToSwitchOff, pageWhereToSwitchOff ]) => {

            const shouldExceptionBeSwitched = (pageWhereToSwitchOff) => {
                if(Array.isArray(pageWhereToSwitchOff)) {
                    return pageWhereToSwitchOff.some(p => p === adjustedFlow[page].pageName);
                } else {
                    return pageWhereToSwitchOff === adjustedFlow[page].pageName;
                }
            };

            if(exceptionTypeToSwitchOff === exceptionType && shouldExceptionBeSwitched(pageWhereToSwitchOff)) {
                props.actions.removeException();
            }
        });
    }, [ exceptionType ]);

    useEffect(() => {
        if(scroll === null) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [ page ]);

    useEffect(() => {

        const isAnyoneFosterCareAgeAndApplyingForCoverage = householdMembers.some(hhm => {
            const age = getDateDifference(serverDate, hhm.dateOfBirth, 'year');
            return age >= 18 && age <= 25 && hhm.applyingForCoverageIndicator;
        });

        const isFullTimeStudent = householdMembers.some(hhm => {
            const age = getDateDifference(serverDate, hhm.dateOfBirth, 'year');
            return age >= 18 && age <= 22 && hhm.applyingForCoverageIndicator;
        });

        const isAnyoneDoesNotResideInTheUSSince1996 = householdMembers.every(hhm => {
            return hhm.citizenshipImmigrationStatus.livedIntheUSSince1996Indicator !== false;
        });

        const prevOutput = cloneDeep(adjustedFlow);

        const output = adjustedFlow.map(el => {

            const hhm = (idx => !~idx ? { name: {} } : householdMembers[el.hhm] ? householdMembers[el.hhm] : { name: {}  })(el.hhm);

            const shouldAskAboutPregnancy = () => {
                return householdMembers.reduce((qty, hhm) => {
                    return getWomanOfReproductiveAge(hhm, serverDate) ? ++qty : qty;
                }, 0);
            };

            const {
                americanIndianAlaskaNative: {
                    americanIndianAlaskaNativeSelfIdentification,
                    memberOfFederallyRecognizedTribeIndicator
                },
                applyingForCoverageIndicator,
                incomes,
                inlineEligibility: {
                    preliminaryAptcCsrStatus,
                    preliminaryChipStatus,
                    preliminaryEmergencyMedicaidStatus,
                    preliminaryMedicaidStatus
                },
                healthCoverage: { currentOtherInsurance },
                personId,
                taxHouseholdComposition: {
                    independentTaxFiler
                }
            } = ~el.hhm && el.hhm <= hhmsQty - 1 ? householdMembers[el.hhm] : {
                americanIndianAlaskaNative: {},
                taxHouseholdComposition: {},
                flagBucket: {},
                inlineEligibility: {},
                healthCoverage: {
                    currentOtherInsurance: {
                        otherStateOrFederalPrograms: []
                    }
                },
                incomes: []
            };

            const checkTribalIncomePage = () => {
                const noEligibleIncomeSources = incomes
                    .filter(filterTools.combinedFilterByType(
                        filterTools.removeDummyType,
                        filterTools.removeSocialSecurityType,
                        filterTools.removeUnemploymentType
                    ))
                    .length === 0;

                const payload = !americanIndianAlaskaNativeSelfIdentification
                    || (americanIndianAlaskaNativeSelfIdentification && noEligibleIncomeSources);

                let explanation;

                switch(true) {
                    case !americanIndianAlaskaNativeSelfIdentification:
                        explanation = 'household member not an AI/AN';
                        break;
                    case noEligibleIncomeSources:
                        explanation = 'household member is AI/AN but no eligible income sources';
                        break;
                    default:
                        explanation = 'something else reason';
                }

                return {
                    payload,
                    explanation
                };
            };

            const checkIfTaxFilersOnApplication = () => {
                return (hhms => hhms.length < 1)(householdMembers
                    .map(hhm => hhm.taxHouseholdComposition.taxFiler ? hhm.personId : null)
                    .filter(el => el));
            };

            const familyStatusForCurrentHhm = (
                s => s
                    ? s.hhmStatus :
                    {
                        currentHhm: {},
                        dependentsOfCaretaker: [],
                        relatedPersonsIds: {}

                    }
            )(allStatuses.find(s => s.hhmGuid === hhm.applicantGuid));

            const checkIfShouldSkipIncomeDiscrepancyPage = () => {
                if(!inlineCurrentIncomeEnabled) {
                    return true;
                }

                const isJobIncomeDiscrepancy = householdMembers.some(hhm => {
                    return hhm.incomes.some(
                        income => (income.type === INCOME_TYPES.JOB || income.type === INCOME_TYPES.DUMMY)
                            && !!income.jobIncomeExplanationRequiredIndicator
                    );
                });

                const isTaxHouseholdIncomeDiscrepancy = primaryTaxFiler
                    ? primaryTaxFiler.annualTaxIncome.incomeExplanationRequiredIndicator
                    : false;

                return !isJobIncomeDiscrepancy && !isTaxHouseholdIncomeDiscrepancy;
            };

            const employerCoveragePageParams = {
                currentOtherInsurance,
                hhm,
                hhmStatus: allStatuses[el.hhm]?.hhmStatus,
                inlineCurrentIncomeEnabled,
                inlineEligibilityIgnoredForEscPage,
                preliminaryAptcCsrStatus,
                serverDate
            };

            const stateEmployeeHealthBenefitPageParams = {
                alwaysDisplayMedicaidQuestions,
                applyingForCoverageIndicator,
                inlineCurrentIncomeEnabled,
                isState,
                preliminaryChipStatus,
                preliminaryEmergencyMedicaidStatus,
                preliminaryMedicaidStatus
            };

            const additionalInformationPageParams = {
                alwaysDisplayMedicaidQuestions,
                applyingForCoverageIndicator,
                familyStatusForCurrentHhm,
                householdMembers,
                inlineCurrentIncomeEnabled,
                isItuQuestionsDisabled,
                isState,
                memberOfFederallyRecognizedTribeIndicator,
                personId,
                preliminaryChipStatus,
                preliminaryEmergencyMedicaidStatus,
                preliminaryMedicaidStatus,
                primaryTaxFilerPersonId
            };

            const medicaidChipDenialInformationPageParams = {
                householdMembers,
                medicaidTerminationFeatureEnabled,
                serverDate,
                medicaidEndDateOverride
            };

            /**
             *
             *
             */

            const conditions = [

                // Skipping "Household relationships" and "Household addresses" pages
                // if the only one household member

                [
                    hhmsQty === 1,
                    [
                        PAGES.OTHER_ADDRESSES_PAGE,
                        PAGES.RELATIONSHIPS_PAGE
                    ],
                    '- only one hhm'
                ],

                // Skipping "Military Service" page if all household members with eligible immigrant status reside in US since 1996

                [
                    isAnyoneDoesNotResideInTheUSSince1996,
                    [
                        PAGES.ACTIVE_MILITARY_VETERAN_PAGE
                    ],
                    '- all household members with eligible immigrant status reside in US since 1996'
                ],

                // Skipping "Citizenship/immigration status", "Other Health Coverage" pages
                // if household member do not seek for coverage

                [
                    !hhm.applyingForCoverageIndicator,
                    [
                        PAGES.CITIZENSHIP_IMMIGRATION_STATUS_PAGE,
                        PAGES.OTHER_HEALTH_COVERAGE_PAGE,
                        PAGES.HEALTH_REIMBURSEMENT_ARRANGEMENT_DETAIL_PAGE
                    ],
                    '- not applied for coverage'
                ],

                // Skipping "Employer Coverage Detail" pages due to several conditions (see: checkEmployerCoverageDetails)

                pageVisibility.checkEmployerCoverageDetailsPage(employerCoveragePageParams),

                // Skipping "Parent/Caretaker" page
                // if household member is not qualified as a caretaker or does not have qualified dependents

                [
                    !familyStatusForCurrentHhm.dependentsOfCaretaker.length,
                    [
                        PAGES.PARENT_CARETAKER_INFORMATION_PAGE
                    ],
                    `- because of caretaker's criteria:
                        -> non-applicant OR 
                        -> age is less than 19 OR
                        -> has no dependents (see below)
                    - because of dependent's criteria:
                        -> age is less than 19 OR
                        -> lives at the same address;
                        -> relationship is not a SPOUSE or FORMER_SPOUSE or DOMESTIC_PARTNER`
                ],

                // Skipping "Tribal income" page
                // if household member does not identify as AI/AN or they do identify but do not have any income

                [
                    checkTribalIncomePage().payload,
                    [
                        PAGES.TRIBAL_SOURCE_INCOME_PAGE
                    ],
                    checkTribalIncomePage().explanation
                ],

                // Skipping "Reconciliation of APTC" page
                // if household member is not primary household contact

                [
                    itfFeatureEnabled
                        ? !(primaryTaxFilerPersonId === personId || independentTaxFiler)
                        : !(primaryTaxFilerPersonId === personId),
                    [
                        PAGES.RECONCILIATION_IN_APTC_PAGE
                    ],
                    itfFeatureEnabled
                        ? '- household member neither Primary Tax Filer nor Independent Tax Filer'
                        : '- applicable only if hhm matches primaryTaxFilerPersonId'
                ],

                // Skipping "Foster care" page
                // if household member's age is not in rage 18 - 25

                [
                    !isAnyoneFosterCareAgeAndApplyingForCoverage,
                    [
                        PAGES.FOSTER_CARE_INFORMATION_PAGE
                    ],
                    '- no one HHM age of 18 - 25 y.o.'
                ],

                // Skipping "FullTime Student" page
                // if applicants's age is not in rage 18 - 22

                [
                    !isFullTimeStudent,
                    [
                        PAGES.FULL_TIME_STUDENT_PAGE
                    ],
                    '- no one HHM age of 18 - 22 y.o.'
                ],

                // Skipping "Pregnancy Information" page
                // if there no any household members who match criteria

                [
                    !shouldAskAboutPregnancy(),
                    [
                        PAGES.PREGNANCY_INFORMATION_PAGE
                    ],
                    '- there are no females age of 9 - 65 y.o.'
                ],

                // Skipping pages for other household members in Income section
                // in review mode started from "Income summary"

                [
                    exceptionType === EXCEPTIONS.REVIEW_INCOME_SECTION
                    && el.hhm > adjustedFlow[exceptionPages[0]].hhm,

                    [
                        PAGES.INCOME_SOURCES_PAGE,
                        PAGES.TRIBAL_SOURCE_INCOME_PAGE,
                        PAGES.DEDUCTION_SOURCES_PAGE,
                        PAGES.EXPECTED_INCOME_PAGE,
                        PAGES.PERSONAL_INCOME_SUMMARY_PAGE
                    ],
                    '- reviewing another household member'
                ],

                // Skipping pages for other household members in Family and Household section
                // in review mode started from "Family and Household summary"

                [
                    exceptionType === EXCEPTIONS.REVIEW_HOUSEHOLD_SECTION
                    && el.hhm > adjustedFlow[exceptionPages[0]].hhm,

                    [
                        PAGES.PERSONAL_INFORMATION_PAGE,
                        PAGES.CITIZENSHIP_IMMIGRATION_STATUS_PAGE,
                        PAGES.TAX_HOUSEHOLD_COMPOSITION_PAGE,
                        PAGES.ETHNICITY_AND_RACE_PAGE
                    ],
                    '- reviewing another household member'
                ],

                // Skipping "Primary Tax Filer" page
                // in case if we don't have 1 or more household members (attested as tax filers)

                [
                    checkIfTaxFilersOnApplication(),
                    [
                        PAGES.PRIMARY_TAX_FILER_PAGE
                    ],
                    '- there are no tax filer on SSAP'
                ],

                // Skipping "State Employee Health Benefit" page
                // in different cases

                pageVisibility.stateEmployeeHealthBenefitPage(stateEmployeeHealthBenefitPageParams),

                // Skipping "Additional Information" page
                // in different cases

                pageVisibility.additionalInformationPage(additionalInformationPageParams),

                // Skipping "Medicaid/CHIP Denial Information" page
                // depending on answers made on "Medicaid Termination Details" page

                pageVisibility.medicaidChipDenialInformationPage(medicaidChipDenialInformationPageParams),

                // Skipping "Marital Status" page
                // in case if household member is younger than 14 y.o. OR married

                [
                    !familyStatusForCurrentHhm.currentHhm.isOlderThanMarriageAge ||
                    familyStatusForCurrentHhm.currentHhm.spouseIndex !== null,
                    [
                        PAGES.MARITAL_STATUS
                    ],
                    '- person is married OR less than 14 y.o.'
                ],

                // Skipping "Income Discrepancy" page
                // No one income of job type does not have jobIncomeExplanationRequiredIndicator === true OR
                // PTF's annualTaxIncome does not have incomeExplanationRequiredIndicator === true

                [
                    checkIfShouldSkipIncomeDiscrepancyPage(),
                    [
                        PAGES.INCOME_DISCREPANCY_PAGE
                    ],
                    '- job type does not have jobIncomeExplanationRequiredIndicator === true OR PTF\'s annualTaxIncome does not have incomeExplanationRequiredIndicator === true'
                ],

                // Skipping "Medicaid Termination Details" page
                // in case if medicaidTerminationFeature disabled

                [
                    !medicaidTerminationFeatureEnabled,
                    [
                        PAGES.MEDICAID_TERMINATION_DETAILS_PAGE
                    ],
                    '- medicaidTerminationFeature comes in settings as \'Disabled\''
                ],

            ];

            let reasons = [];

            conditions.map(([ value, pages, message ]) => {
                if(value && pages.some(page => page === el.pageName)) {
                    if(Array.isArray(message)) {
                        reasons = [
                            ...reasons,
                            ...message
                        ];
                    } else {
                        reasons = [
                            ...reasons,
                            message
                        ];
                    }
                }
            });

            return {
                ...el,
                isActive: !reasons.length > 0,
                reasons
            };
        });

        const isItEqual = isEqual(prevOutput, output);

        props.actions.adjustFlow(output, isItEqual);
    }, [ debugWatchedProps(watchedProps, true) ]);

    useEffect(() => {
        if(!flowCreated) {
            return;
        }

        const {
            page: {
                asyncQueries
            }
        } = preventerTools.provideObject(adjustedFlow[page], { page: { } });

        const callbacks = { togglePageReadiness, toggleExtraDataStatus };

        if(asyncQueries && isExtraDataRetrieved === null) {
            togglePageReadiness(false);
            switch(asyncQueries.queryType) {
                case SSNS:
                    props.actions.receiveSSNs(callbacks);
                    break;
                case IMMIGRATION_INFO:
                    props.actions.receiveImmigrationInfo(callbacks);
                    break;
                case COMMUNICATION_PREFERENCES:
                    props.actions.fetchCommunicationPreferences(callbacks);
                    break;
                default:
                    return null;
            }

        } else {
            // If no async queries or already loaded, set page readiness to true
            if (!asyncQueries || isExtraDataRetrieved) {
                togglePageReadiness(true);
            }
        }
    }, [ adjustedFlow.length, flowCreated ]);

    if(adjustedFlow.length === 0) {
        return null;
    }

    let CurrentPage;

    let CurrentControls;

    if(isFlowLoaded) {
        try {
            if(adjustedFlow[page].isActive === false && !(direction === 'exact')) {
                let closestEligiblePage = null;
                switch(direction) {
                    case 'increase':
                        closestEligiblePage = adjustedFlow.findIndex((p, idx) => p.isActive && idx > page);
                        break;
                    case 'decrease':
                        closestEligiblePage = adjustedFlow.reduce((closestWithLessIndexPage, p, idx) => {
                            return p.isActive && idx < page ? idx : closestWithLessIndexPage;
                        }, null);
                }
                if(exceptionType && adjustedFlow[exceptionPages[0]].isActive === false ) {
                    props.actions.updateException([ page + 1 ]);
                }
                props.actions.changePage(closestEligiblePage);
                return null;
            }

            if(adjustedFlow[page].hhm !== -1 && currentHhm === undefined) {
                return null;
            }

            const {
                component: Component,
                pageName,
                formName
            } = adjustedFlow[page].page;

            const isUnemploymentIncomeAttestationEnabled =
                UNEMPLOYMENT_INCOME_ATTESTATION_SUPPORTED_YEARS.some(y => y === coverageYear);

            const statusesForCommonPage = adjustedFlow[page].hhm === -1 ? { allStatuses } : {};

            CurrentPage = isPageDataReady ? React.cloneElement(Component,
                {
                    ...statusesForCommonPage,
                    buttonContainer: ButtonContainer,
                    pageName: pageName,
                    familyStatus,
                    formName,
                    history,
                    hhmIndex,
                    currentHhm,
                    isUnemploymentIncomeAttestationEnabled,
                    inlineCurrentIncomeEnabled,
                    itfFeatureEnabled,
                    medicaidTerminationFeatureEnabled,
                    memberRemovalReasonEnabled,
                    addressDefaults: {
                        defaultHomeAddress,
                        defaultMailingAddress,
                        defaultHomeAddressId,
                        defaultMailingAddressId
                    },
                    alwaysDisplayMedicaidQuestions
                }) : null;

            CurrentControls = React.cloneElement(<ControlsContainer />,
                {
                    disabledUi,
                    formName,
                    history
                });
        } catch(e) {
            props.actions.setError({
                errorMessage: 'Wrong page number (it does not exist in current flow) has been provided',
                errorType: 'Handling flows'
            });
        }
    }

    return (
        <div className="grid-container padding-x-0 fade-in page-container">
            { CurrentPage }
            { CurrentControls }
        </div>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,

    adjustedFlow: state.ssap.adjustedFlow,
    bloodRelationshipCodes: state.ssap.relations.codes,
    data: state.ssap.data,
    direction: state.ssap.direction,
    disabledUi: state.ssap.disabledUi,
    exception: state.ssap.exception,
    flow: state.ssap.flow,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember,

    launchData: state.ssap.launchData,

    key: `page_key_${state.ssap.page}`, // For fade-in updating then page is changed

    page: state.ssap.page,
    loadedRelations: state.ssap.relations.loadedRelations,
    scroll: state.ssap.scroll
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        adjustFlow,
        changePage,
        fetchCommunicationPreferences,
        receiveImmigrationInfo,
        receiveSSNs,
        removeException,
        setError,
        updateException
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(PagesContainer);
